import type { FeedbackKind, IssueType, MeDto } from '@nsb/core';
import { createContext, useContext, useEffect } from 'react';
import { isStaff } from '../session.tsx';

/** NSB's own choices: org staff, and an admin seeing the portal as someone else (it is still them reporting). */
export const reportsAsStaff = (me: MeDto | null | undefined) => isStaff(me) || Boolean(me?.viewAs);

/** The words on the button at the bottom right, for the person reading: the notice at the top names it by them. */
export const triggerWords = (me: MeDto | null | undefined) => (reportsAsStaff(me) ? 'Feedback' : 'Ask or report');

export interface FeedbackRecord {
  type: string;
  id: string;
  label: string;
}

export interface FeedbackState {
  projectId: string | null;
  pageRecord: FeedbackRecord | null;
  setPage: (projectId: string | null, record: FeedbackRecord | null) => void;
  /** Open the sheet: on a kind, about a record (the page's unless given), already a bug or a feature if said. */
  open: (kind?: FeedbackKind, record?: FeedbackRecord | null, issueType?: IssueType) => void;
  /** Whether how to report (the notice at the top) is open: the button at the bottom right is pointed at meanwhile. */
  how: boolean;
  showHow: (open: boolean) => void;
}

export const FeedbackContext = createContext<FeedbackState | null>(null);

export function useFeedback(): FeedbackState {
  const c = useContext(FeedbackContext);
  if (!c) throw new Error('FeedbackProvider missing');
  return c;
}

/** Declare what this screen is about: its project, and the record on screen (if one). */
export function useFeedbackRecord(projectId: string | null, record: FeedbackRecord | null): void {
  const { setPage } = useFeedback();
  const type = record?.type ?? null;
  const id = record?.id ?? null;
  const label = record?.label ?? null;
  useEffect(() => {
    setPage(projectId, type !== null && id !== null && label !== null ? { type, id, label } : null);
    return () => setPage(null, null);
  }, [projectId, type, id, label, setPage]);
}
